import { type FC, useCallback, useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { toast } from "sonner";
import { Button } from "@ui/Button";
import { Form } from "@ui/Form";
import { formSessionAtom } from "@/state/formSession";
import { MultiStepFormProvider } from "@/providers";
import type { FormStep1EnergyConsumption } from "@/types/Form";
import { useMultiStepForm } from "@/hooks";
import { isAlreadyPaidCheckoutError } from "@/utils/errors";
import { getFormSteps } from "@/utils/formConfig";
import { setPaymentSessionStore } from "@/utils/formSession";
import { FormApi } from "@/api/FormApi";
import { PaymentsApi } from "@/api/PaymentsApi";
import { SummaryCard1, SummaryCard2, SummaryCard3, SummaryCard4 } from "@/components/Cards";
import SummaryLayout from "./SummaryLayout";

export const Summary: FC = () => {
  const { t } = useTranslation();

  return (
    <MultiStepFormProvider isSummary steps={getFormSteps(t)}>
      <SummaryContent />
    </MultiStepFormProvider>
  );
};

const SummaryContent: FC = () => {
  const { form } = useMultiStepForm();
  const { formId, customerId } = useAtomValue(formSessionAtom);
  const { t } = useTranslation();
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!checkoutStarted || window.parent === window) return;

    let timeoutId: number | undefined;
    let rafId1 = 0;
    let rafId2 = 0;

    const sendHeight = () => {
      const height = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight,
        document.documentElement.offsetHeight,
        document.body.offsetHeight,
      );
      window.parent.postMessage({ height }, "*");
    };

    // Fire immediately, then after paints/transitions for step-change reliability.
    sendHeight();
    rafId1 = requestAnimationFrame(sendHeight);
    rafId2 = requestAnimationFrame(() => {
      timeoutId = window.setTimeout(sendHeight, 120);
    });

    return () => {
      cancelAnimationFrame(rafId1);
      cancelAnimationFrame(rafId2);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [checkoutStarted, checkoutUrl]);

  const { mutateAsync: createCheckout, isPending } = useMutation({
    mutationFn: () => PaymentsApi.createCheckoutSession(formId ?? ""),
  });

  const { mutateAsync: addProfile } = useMutation({
    mutationFn: (file: File) =>
      FormApi.patchStep1EnergyConsumption(formId ?? "", {
        file_input: file,
        profile_completion_form: "FILE_INPUT",
      } as FormStep1EnergyConsumption),
  });

  const prepareCheckoutWindow = useCallback(() => {
    const checkoutWindow = window.open("", "_blank");

    if (!checkoutWindow) {
      toast.error(t("checkoutSessionError"));
      return null;
    }

    checkoutWindow.name = JSON.stringify({
      formId,
      customerId,
    });

    return checkoutWindow;
  }, [customerId, formId, t]);

  const navigateCheckoutWindow = useCallback((checkoutWindow: Window, url: string) => {
    checkoutWindow.location.href = url;
  }, []);

  const handlePay = async () => {
    const checkoutWindow = prepareCheckoutWindow();
    if (!checkoutWindow) return;

    try {
      const file = form.getValues().summary.excelProfile;

      if (file) {
        try {
          await addProfile(file);
        } catch {
          checkoutWindow.close();
          toast.error(t("excelProfileUploadError"));
          return;
        }
      }

      const checkout = await createCheckout();
      setPaymentSessionStore({ formId, customerId });
      navigateCheckoutWindow(checkoutWindow, checkout.checkout_url);

      setCheckoutUrl(checkout.checkout_url);
      setCheckoutStarted(true);
    } catch (error) {
      checkoutWindow.close();
      toast.error(
        isAlreadyPaidCheckoutError(error)
          ? t("checkoutAlreadyPaidError")
          : t("checkoutSessionError"),
      );
    }
  };

  const handleOpenPaymentAgain = () => {
    if (!checkoutUrl) return;
    const checkoutWindow = prepareCheckoutWindow();
    if (!checkoutWindow) return;
    navigateCheckoutWindow(checkoutWindow, checkoutUrl);
  };

  if (checkoutStarted) {
    return (
      <div
        className="flex w-full flex-col items-center justify-center gap-6 text-center"
        data-test="checkout-started-section"
      >
        <h1 className="text-xl font-bold sm:text-2xl lg:text-[34px]">{t("checkoutOpenedTitle")}</h1>
        <p className="max-w-[640px] text-base lg:text-lg">{t("checkoutOpenedDescription")}</p>

        {checkoutUrl && (
          <Button type="button" onClick={handleOpenPaymentAgain} className="uppercase">
            {t("openPaymentAgain")}
          </Button>
        )}
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <SummaryLayout onPayment={() => void handlePay()} isLoadingSession={isPending}>
          <SummaryCard1 />
          <SummaryCard2 />
          <SummaryCard3 />
          <SummaryCard4 />
        </SummaryLayout>
      </Form>
    </FormProvider>
  );
};

export default SummaryContent;
